import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild, HostListener} from '@angular/core';
import * as moment from 'moment';
import {UserMappingService} from '../services/usermapping.service';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {CreateWorkScheduleComponent} from './create-work-schedule.component';
import {ScheduleService} from '../services/schedule.service';
import {Schedule, ScheduleListUpdates} from '../model/schedule';
import {EditWorkScheduleComponent} from './edit-work-schedule.component';
import {HeromaImporterComponent} from './heroma-importer.component';
import {Subscription} from 'rxjs';
import {WorkShiftService} from '../services/workShift.service';
import {UserCheckIn} from '../model/userCheckIn';
import {List} from 'immutable';
import {WorkShiftGroup} from '../model/workShiftGroup';
import {Availability} from '../model/availability';
import {SettingsService} from '../services/settings.service';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ConfirmDialog } from '../confirmation/confirm.dialog';
import { ToastrService } from "ngx-toastr";
import { BusService } from '../services/bus.service';
import { Bus } from '../model/bus';
import { DateTimeUtils } from '../shared/datetimeutils';
import { BusWithOverlaps } from '../model/busWithOverlap';
import { SPINNER_ANIMATIONS, SPINNER_PLACEMENT, ISpinnerConfig, NgxSpinner } from '@hardpool/ngx-spinner';
import { HasUnsavedChanges, UnsavedChangesGuard } from '../services/unsavedChangesGuard.service';

@Component({
  selector: 'work-schedule',
  templateUrl: 'work-schedule.component.html',
  styleUrls: ['work-schedule.component.css'],
})
export class WorkScheduleComponent implements OnInit, OnDestroy, AfterViewInit, HasUnsavedChanges {


  private checkedInUserSubscription: Subscription;
  private updateWorkShiftSubscription: Subscription;
  private checkedInUsers: UserCheckIn[] = [];

  public schedules: Schedule[];
  public originalSchedules: Schedule[];
  public displayedColumns = ['checked-in-status', 'skill', 'name', 'buss', 'start', 'activityName', 'comment', 'delete'];
  public dataSource: MatTableDataSource<Schedule>;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("spinner", { static: true }) spinner: NgxSpinner;
  spinnerConfig: ISpinnerConfig;

  public todayString: string = moment(new Date()).format("YYYY-MM-DD");
  public activeDate: string;
  public plannedDates: string[];
  public maxDate: string;
  confirmModalRef: MatDialogRef<ConfirmDialog>;

  public buses: Bus[];
  public timeListFrom: string[];
  public timeListTo: string[];
  private readonly noBusSelected: string = "Ingen buss";
  public schedulesWithChanges: Schedule[] = [];
  private static readonly lateToWorkThresholdMinutes: number = 5;
  private lateToWorkTimer: any;

  constructor(
    private readonly usermappingService: UserMappingService,
    private readonly dialog: MatDialog,
    private readonly scheduleService: ScheduleService,
    private readonly workShiftService: WorkShiftService,
    private readonly settingsService: SettingsService,
    private readonly toastr: ToastrService,
    private readonly busService: BusService,) {

    this.activeDate = this.todayString;

    this.plannedDates = [];
    this.plannedDates.push(this.todayString);
    this.plannedDates.push(moment(new Date()).add(-1, 'days').format("YYYY-MM-DD"))

    this.maxDate = this.todayString;
    this.timeListFrom = DateTimeUtils.getTimeArrayQuartersFrom();
    this.timeListTo = DateTimeUtils.getTimeArrayQuartersTo();

  }

  ngOnInit(): void {
    const today = moment().format("YYYY-MM-DD");
    this.scheduleService.getSchedules(today).then((schedules: Schedule[]) => {

      this.schedules = Schedule.ToTypeSafeList(schedules);
      this.originalSchedules = Schedule.ToTypeSafeList(schedules);
      this.dataSource = new MatTableDataSource(this.schedules);
      this.sortScheduleList();
     

      this.checkedInUserSubscription = this.usermappingService.CheckedInUsers.subscribe((result: List<UserCheckIn>) => {
        this.checkedInUsers = result.toArray();
        this.checkedInUsers.forEach(checkedIn => this.setUserMappingIdCheckedInStatus(checkedIn.usermappingId, checkedIn.isCheckedIn));
        this.checkLateForWorkStatus();
      });
    });

    this.updateWorkShiftSubscription = this.workShiftService.workShiftGroupsUpdated$.subscribe(() => {
      this.onUpdateWorkShiftGroups(this.workShiftService.workShiftGroups);
    });

    

    this.startCheckinStatusVerification();
    this.getBuses();

    this.spinnerConfig = {
      placement: SPINNER_PLACEMENT.block_ui,
      animation: SPINNER_ANIMATIONS.horizontal_bounce,
      size: "2rem",
      color: "#7AB800",
      bgColor: "transparent"
    };
  }

  ngAfterViewInit() {
    let self = this;
    setTimeout(() => {
      self.dataSource.sort = this.sort
    }, 2000);
  }

  ngOnDestroy(): void {
    this.checkedInUserSubscription.unsubscribe();
    this.updateWorkShiftSubscription.unsubscribe();
    clearInterval(this.lateToWorkTimer);
  }

  public getReadableSkill(skill: string) {
    if (skill !== undefined) {
      let rx = /[a-zA-Z ]+/;
      let readableSkill = rx.exec(skill);
      return readableSkill[0];
    }
    return undefined;
  }

  public getScheduleTimeSpan(schedule: Schedule) {
    const start = moment(schedule.start);
    const end = moment(schedule.end);
    return start.format("HH:mm") + " - " + end.format("HH:mm");

  }

  public async onDateClicked(date: Date | null) {



    const previousDate = this.activeDate;
    const dateKey = moment(date).format("YYYY-MM-DD");

    if (this.hasUnsavedChanges()) {
      this.confirmModalRef = this.dialog.open(ConfirmDialog, {
        disableClose: false,
        width: UnsavedChangesGuard.DialogWidth,
        autoFocus: false,
        closeOnNavigation: false,
        data: UnsavedChangesGuard.ConfirmDialogData
      });

      const shouldContinue = await this.confirmModalRef.afterClosed().toPromise();
      if (!shouldContinue) {
        this.activeDate = previousDate;
        console.log(this.activeDate);

        return;
      }
    }

    this.activeDate = dateKey;
    await this.scheduleService.getSchedules(dateKey).then((schedules: Schedule[]) => {
      this.schedules = Schedule.ToTypeSafeList(schedules);
      this.originalSchedules = Schedule.ToTypeSafeList(schedules);
      this.schedulesWithChanges = [];
    });
    this.sortScheduleList();
    this.checkLateForWorkStatus();
  }

  public canEdit(): boolean {
    let currentDate = new Date();

    let diffFromCurrentDate = moment(new Date(this.activeDate)).diff(currentDate, 'days');
    return diffFromCurrentDate >= 0;
  }

  public onRowClick(schedule: Schedule) {

    if (!this.canEdit()) { return; }

    schedule.airportId = this.settingsService.getCurrentAirport();

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = "600px";
    dialogConfig.panelClass = "";
    dialogConfig.data = {
      schedule: schedule,
      activeDate: this.activeDate
    };
    this.dialog.open(EditWorkScheduleComponent, dialogConfig).afterClosed().subscribe((dialogResult: DialogResult) => {
      console.log("Updated: " + dialogResult);

      if (dialogResult?.schedule === undefined) return;

      const index = this.schedules.findIndex((sch: Schedule) => sch.id == dialogResult.schedule.id);

      if (index >= 0) {
        this.schedules.splice(index, 1);
        if (dialogResult.deleted) {
          this.schedules = this.schedules.concat([]); //concat to get angular to refresh UI
          
        }
        else {
          this.schedules = this.schedules.concat([dialogResult.schedule]); //concat to get angular to refresh UI
        }
        this.sortScheduleList();
       
      }
    });


  }

  public onAddClick() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = "600px";
    dialogConfig.panelClass = "";
    dialogConfig.data = {
      userMapping: undefined,
      airportId: this.settingsService.getCurrentAirport(),
      activeDate: this.activeDate
    };

    this.dialog.open(CreateWorkScheduleComponent, dialogConfig)
      .afterClosed().subscribe((newSchedule: Schedule) => {
        if (newSchedule) {

          if (this.schedules.find(s => s.id === newSchedule.id)) return;

          this.schedules = this.schedules.concat([newSchedule]); //concat to get angular to refresh UI
          this.originalSchedules = this.originalSchedules.concat([newSchedule]);
          this.sortScheduleList();
        }
      });
  }

  public onImportFromHeromaClick() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = "600px";
    dialogConfig.data = {
    };
    this.dialog.open(HeromaImporterComponent, dialogConfig)
      .afterClosed().subscribe(() => {
        console.log("Closed");
      });

  }

  public buttonDeleteCallback(event: any, schedule: Schedule) {
    event.stopPropagation();

    this.confirmModalRef = this.dialog.open(ConfirmDialog, {
      disableClose: false,
      width: "400px",
      autoFocus: false,
      closeOnNavigation: false, data:
      {
        id: 1,
        title: "Bekräfta",
        text: "Vill du verkligen radera  schemat för " + schedule.name + "?<br>"
      }
    });

    this.confirmModalRef.afterClosed().subscribe(result => {
      if (result) {
        this.scheduleService.deleteSchedule(schedule)
          .then(() => {
            this.toastr.success("Schemarad raderat");
          })
          .catch((response) => this.toastr.error("Det gick inte att radera schemat. " + response.error))
      }
    });

  }

  public getRowClass(schedule: Schedule): string {
    try {
      const skills = JSON.parse(schedule.skill) as string[];
      if (skills.some(s => s.toUpperCase() === 'AIR')) {
        return 'airside-row';
      }
      if (skills.some(s => s.toUpperCase() === 'LAND')) {
        return 'landside-row'
      }
    } catch {
      // If skill parsing fails, return empty
    }
    return '';
  }

  private createScheduleFromAvailability(availability: Availability): Schedule{

    let schedule = new Schedule();

    schedule.id = availability.id;
    schedule.userMappingId = availability.userMappingID;
    schedule.name = availability.name;
    schedule.activityName = availability.metaExpanded.ActivityName;
    schedule.buss = availability.metaExpanded.BusNumber;
    schedule.comment = availability.comment;
    schedule.start = availability.start;
    schedule.end = availability.end;
    schedule.skill = availability.skill;

    return schedule;
  }

  private onUpdateWorkShiftGroups(workShiftGroups: WorkShiftGroup[]) {

    console.log("onUpdateWorkShiftGroups");

    let activeDateKey = moment(new Date(this.activeDate)).format("YYYYMMDD");

    workShiftGroups.forEach(wsg => {
      wsg.workShifts.forEach(ws => {
        ws.availabilities = ws.availabilities.filter(a => a.dateKey === activeDateKey);
      });
    });

    let availabilities = this.workShiftService.getAvailabilitiesForWorkShiftGroups(workShiftGroups);
    availabilities = availabilities.filter(a => a.dateKey === activeDateKey);
    let updatedSchedulesList: Schedule[] = [];

    availabilities.forEach((a) => {
      let schedule = this.schedules.find(s => s.id == a.id);

      if (schedule === undefined) {
        updatedSchedulesList.push(this.createScheduleFromAvailability(a));
      } else {
        schedule.start = a.start;
        schedule.end = a.end;
        schedule.skill = a.skill;
        schedule.buss = a.metaExpanded.BusNumber;
        schedule.activityName = a.metaExpanded.ActivityName;
        schedule.comment = a.comment;

        updatedSchedulesList.push(schedule);
      }
    });

    // Assign outside the loop so an empty result clears the list
    this.schedules = updatedSchedulesList;
    this.originalSchedules = Schedule.ToTypeSafeList(updatedSchedulesList);

    this.applySchedulesWithChanges();
    this.sortScheduleList();
  }

  private setUserMappingIdCheckedInStatus(userMappingId: string, status: boolean) {

    if (this.schedules === undefined) return;
    const r = this.schedules.filter(a => a.userMappingId === userMappingId);
    if (r === undefined) return;
    r.forEach((scheduleItem) => scheduleItem.checkedin = status);

  }

  private setUserMappingLateToWork(userMappingId: string) {
    const r = this.schedules.filter(a => a.userMappingId === userMappingId);
    if (r === undefined) return;
    r.forEach((scheduleItem) => scheduleItem.lateToWork = true);
  }

  private sortScheduleList() {

    const hasSkill = (schedule: Schedule, skillName: string): boolean => {
      try {
        const skills = JSON.parse(schedule.skill) as string[];
        return skills.some(s => s.toUpperCase() === skillName);
      } catch {
        return false;
      }
    };

    const getPriority = (activityName: string): number => {
      if (!activityName || activityName.length < 2) return 4;

      const firstChar = activityName[0].toUpperCase();
      const secondChar = activityName[1];

      // Only match if followed by a digit (like C# char.IsDigit check)
      if (!/\d/.test(secondChar)) return 4;

      switch (firstChar) {
        case 'M': return 0;
        case 'D': return 1;
        case 'K': return 2;
        case 'N': return 3;
        default: return 4;
      }
    };

    this.schedules.sort((a, b) => {
      const aHasLand = hasSkill(a, 'LAND');
      const bHasLand = hasSkill(b, 'LAND');
      const aHasAir = hasSkill(a, 'AIR');
      const bHasAir = hasSkill(b, 'AIR');

      // Group 1: LAND schedules come first
      if (aHasLand && !bHasLand) return -1;
      if (!aHasLand && bHasLand) return 1;

      // Both LAND: sort by activity priority (M, D, K, N), then characters, then start
      if (aHasLand && bHasLand) {
        const priorityA = getPriority(a.activityName);
        const priorityB = getPriority(b.activityName);

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        // Same priority letter: sort by full activity name (e.g. M1 before M2)
        const nameCompare = a.activityName.localeCompare(b.activityName);
        if (nameCompare !== 0) return nameCompare;

        // Exact same activity name: sort by start time
        if (a.start < b.start) return -1;
        if (a.start > b.start) return 1;
        return 0;
      }

      // Group 2: AIR schedules come after LAND
      if (aHasAir && !bHasAir && !bHasLand) return -1;
      if (!aHasAir && bHasAir && !aHasLand) return 1;

      // Both AIR (or both other): sort by start time, then name
      if (a.start < b.start) return -1;
      if (a.start > b.start) return 1;
      return a.name.localeCompare(b.name);
    });

    this.checkLateForWorkStatus();
    this.rebindDataSource();
  }

  private rebindDataSource() {

    if (this.dataSource !== undefined) {
      this.dataSource.data = this.schedules;
    }
  }

  private verifyIfStartTimeIsClose(schedule: Schedule): void {
    const diffInSeconds = moment(schedule.start).diff(moment(), 'seconds');
    schedule.lateToWork = diffInSeconds > 0
      && diffInSeconds <= WorkScheduleComponent.lateToWorkThresholdMinutes * 60;
  }

  private checkLateForWorkStatus() {
    if (this.checkedInUsers !== undefined && this.checkedInUsers.length > 0) {
      this.checkedInUsers.forEach(user => {
        this.setUserMappingIdCheckedInStatus(user.usermappingId, user.isCheckedIn);
      });
    }

    if (this.schedules === undefined) return;

    this.schedules.forEach((schedule: Schedule) => {
      if (schedule.checkedin) {
        schedule.lateToWork = false;
        return;
      }
      this.verifyIfStartTimeIsClose(schedule);
    });
  }

  private startCheckinStatusVerification() {
    this.checkLateForWorkStatus();
    this.lateToWorkTimer = setInterval(() => {
      this.checkLateForWorkStatus();
    }, 15000);
  }

  private getTimeString(date: Date) {
    const time = moment(date);
    return time.format("HH:mm");
  }

  private getBuses(): void {
    this.busService.getAllBuses().then((buses: Bus[]) => {
        this.buses = [...buses];
        let bus = new Bus();
        bus.number = this.noBusSelected;
        this.buses.unshift(bus);
        this.buses.sort((b1: Bus, b2: Bus) => +b1.number - +b2.number);
    });
  }

  private changeBus(schedule: Schedule, event: any): void {
    schedule.buss = event;
    this.checkScheduleChanged(schedule);
  }

  private changeStart(schedule: Schedule, event: any): void {
    let startTime = event.value;
    const startDateTime = DateTimeUtils.convertStringToDateFrom(this.activeDate, startTime);
    schedule.start = startDateTime;
    this.checkScheduleChanged(schedule);
  }

  private changeEnd(schedule: Schedule, event: any): void {
    let endTime = event.value;
    const endDateTime = DateTimeUtils.convertStringToDateTo(this.activeDate, endTime);
    schedule.end = endDateTime;
    this.checkScheduleChanged(schedule);
  }

  private checkScheduleChanged(schedule: Schedule): void {
    // If schedule comment is empty string, set to undefined as this is what we get from api
    schedule.comment = schedule.comment || undefined;

    // Find the original schedule so we know if anything has changes
    const originalSchedule = this.originalSchedules.find(s => s.id === schedule.id);

    let hasChanged = false;

    // Check if Bus, start time, end time or comment has changed.
    if (schedule.buss !== originalSchedule.buss
      || this.getTimeString(schedule.start) !== this.getTimeString(originalSchedule.start)
      || this.getTimeString(schedule.end) !== this.getTimeString(originalSchedule.end)
      || schedule.comment !== originalSchedule.comment) {
      hasChanged = true;
    }

    // If we have changes
    if (hasChanged) {
      const existingIndex = this.schedulesWithChanges.findIndex(x => x.id === schedule.id);
      if (existingIndex >= 0) {
        // Update existing entry
        this.schedulesWithChanges[existingIndex] = schedule;
      } else {
        // Add new entry
        this.schedulesWithChanges.push(schedule);
      }
    } else {
      // Nothing changed compared to original
      const existingIndex = this.schedulesWithChanges.findIndex(x => x.id === schedule.id);
      // If schedule exists in schedulesWithChanges, it has most likely changed back to the original, thus, we remove it from the list
      if (existingIndex >= 0) {
        this.schedulesWithChanges.splice(existingIndex, 1);
      }
    }
  }

  private hasScheduleChanges(): boolean {
    return this.schedulesWithChanges.length > 0;
  }

  private rowHasChanges(schedule: Schedule) {
    const existingIndex = this.schedulesWithChanges.findIndex(x => x.id === schedule.id);

    return existingIndex >= 0;
  }

  private undoChangesToRow(schedule: Schedule) {
    const originalSchedule = this.originalSchedules.find(s => s.id === schedule.id);
    const scheduleIndex = this.schedules.findIndex(x => x.id == originalSchedule.id);

    if (scheduleIndex >= 0) {
      const schedule = this.schedules[scheduleIndex];
      schedule.comment = originalSchedule.comment;
      schedule.buss = originalSchedule.buss;
      schedule.start = originalSchedule.start;
      schedule.end = originalSchedule.end;

      const existingIndex = this.schedulesWithChanges.findIndex(x => x.id === schedule.id);
      if (existingIndex >= 0) {
        this.schedulesWithChanges.splice(existingIndex, 1);
      }
    }
  }

  private undoChangedToAllSchedules() {
    const schedulesToUndo = [...this.schedulesWithChanges];
    schedulesToUndo.forEach((schedule) => {
      this.undoChangesToRow(schedule);
    });
  }

  private async onSaveSchedulesClick(): Promise<void> {

    this.spinner.show();
    let schedulesToUpdate = [...this.schedulesWithChanges];

    // Validate buses before saving
    for (const schedule of schedulesToUpdate) {
      await this.busService.getAvailableBuses(schedule.id, schedule.start, schedule.end)
        .then((buses: BusWithOverlaps[]) => {
          const validBus = buses.find((bus: BusWithOverlaps) => bus.number === schedule.buss);
          const noBusSelected = schedule.buss == this.noBusSelected;
          const isUnknownBus = schedule.buss == "Okänd";

          if (!validBus && !noBusSelected && !isUnknownBus) {
            this.toastr.error("Bus " + schedule.buss + " is not available for " + schedule.name);
            const schedulesToUpdateIndex = schedulesToUpdate.findIndex((sch: Schedule) => sch.id == schedule.id);

            if (schedulesToUpdateIndex >= 0) {
              schedulesToUpdate.splice(schedulesToUpdateIndex, 1);
            }
          }
        });
    }
    if (schedulesToUpdate.length > 0) {
      schedulesToUpdate.forEach((schedule) => {
        schedule.airportId = this.settingsService.getCurrentAirport();
      });

      await this.scheduleService.updateSchedulesList(schedulesToUpdate)
        .then((listUpdates: ScheduleListUpdates) => {

          if (listUpdates.successfulUpdates.length > 0) {
            this.toastr.success("Uppdaterade " + listUpdates.successfulUpdates.length + " scheman");
          }

          if (listUpdates.failedUpdates.length > 0) {
            let toastrErrorMessage = "";

            listUpdates.failedUpdates.forEach((failedSchedule) => {
              toastrErrorMessage += failedSchedule.schedule.name + " : " + failedSchedule.errorMessage + " </br></br>";
            });

            this.toastr.error(toastrErrorMessage, "Error");
          }

          // Remove the old schedule and add the updated schedules to the arrays
          if (listUpdates.successfulUpdates.length > 0) {
            listUpdates.successfulUpdates.forEach((schedule) => {
              const originalIndex = this.originalSchedules.findIndex((sch: Schedule) => sch.id == schedule.id);
              const scheduleWithChangesIndex = this.schedulesWithChanges.findIndex((sch: Schedule) => sch.id == schedule.id);

              if (originalIndex >= 0) {
                this.originalSchedules.splice(originalIndex, 1);
              }
              if (scheduleWithChangesIndex >= 0) {
                this.schedulesWithChanges.splice(scheduleWithChangesIndex, 1);
              }
            });

            this.originalSchedules = this.originalSchedules.concat(listUpdates.successfulUpdates);
          }


          this.spinner.hide();
        })
        .finally(() => {
          this.spinner.hide();
        });

    }
    else {

      this.spinner.hide();
    }
  }

  private applySchedulesWithChanges() {
    if (this.schedulesWithChanges.length > 0) {
      this.schedulesWithChanges.forEach((changedSchedule) => {
        const scheduleIndex = this.schedules.findIndex((sch: Schedule) => sch.id == changedSchedule.id);
        if (scheduleIndex >= 0) {
          this.schedules.splice(scheduleIndex, 1);
        }
      });

      this.schedules = this.schedules.concat(this.schedulesWithChanges);
    }
  }


  hasUnsavedChanges(): boolean {
    if (this.schedulesWithChanges.length > 0) {
      return true;
    }
     return false;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges()) {
      return;
    }
    // Required to trigger the browser's confirmation dialog
    event.preventDefault();

    // Legacy-but-required for most browsers to show the confirmation dialog.
    // TypeScript marks it deprecated, but this is still the practical way.
    event.returnValue = '';
  }

}

export class DialogResult {
  schedule: Schedule;
  deleted: boolean;
}
